/**
 * React Query hooks for Test Cases API
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { testCasesApi } from '../services/testcases';
import type { TestCase, TestCaseStatus, ExcelFileGroup, ProjectGroup } from '../types/domain';
import { normalizeStatus } from '../lib/status-config';

// =============================================================================
// Query Keys
// =============================================================================

type DashboardKeyParams = {
  userId?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  dueFilter?: string;
  view?: string;
  status?: string;
  projectId?: string;
  scenario?: string[];
  search?: string;
};

export const testCasesKeys = {
  all: ['testCases'] as const,
  dashboardAll: () => [...testCasesKeys.all, 'dashboard'] as const,
  dashboard: (params?: DashboardKeyParams) =>
    [...testCasesKeys.all, 'dashboard', params ?? {}] as const,
  detail: (id: string) => [...testCasesKeys.all, 'detail', id] as const,
  byProject: (projectId: string) => [...testCasesKeys.all, 'project', projectId] as const,
  byStatus: (status: TestCaseStatus) => [...testCasesKeys.all, 'status', status] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Hook to fetch tester dashboard with assigned test cases
 *
 * Usage:
 * ```
 * const { data, isLoading } = useTesterDashboard({
 *   status: 'in_progress',
 *   refetchInterval: 10000, // Poll every 10s
 * });
 * ```
 */
export function useTesterDashboard(options?: {
  status?: 'all' | TestCaseStatus | TestCaseStatus[];
  projectId?: string | string[];
  scenario?: string | string[];
  search?: string;
  userId?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  dueFilter?: 'all' | 'due' | 'overdue';
  view?: 'actionable' | 'all';
  enabled?: boolean;
  refetchInterval?: number;
}) {
  const scenarioKey = Array.isArray(options?.scenario)
    ? options?.scenario
    : options?.scenario
      ? [options.scenario]
      : undefined;
  const projectKey = Array.isArray(options?.projectId)
    ? options.projectId.join(',')
    : options?.projectId;
  const statusKey = Array.isArray(options?.status)
    ? options.status.join(',')
    : options?.status;

  return useQuery({
    queryKey: testCasesKeys.dashboard({
      userId: options?.userId,
      page: options?.page,
      pageSize: options?.pageSize,
      sort: options?.sort,
      dueFilter: options?.dueFilter,
      view: options?.view,
      status: statusKey,
      projectId: projectKey,
      scenario: scenarioKey,
      search: options?.search,
    }),
    queryFn: () => testCasesApi.getDashboard({
      status: options?.status,
      project: options?.projectId,
      scenario: options?.scenario,
      search: options?.search,
      userId: options?.userId,
      page: options?.page,
      pageSize: options?.pageSize,
      sort: options?.sort,
      dueFilter: options?.dueFilter,
      view: options?.view,
    }),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    staleTime: 30000, // 30 seconds
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single test case by ID
 * Note: Currently uses the dashboard API to get the test case
 */
export function useTestCase(testCaseId: string) {
  return useQuery({
    queryKey: testCasesKeys.detail(testCaseId),
    queryFn: async () => {
      // Fetch dashboard data and find the specific test case
      const dashboard = await testCasesApi.getDashboard();
      const testCase = dashboard.test_cases.find(tc => tc.id === testCaseId);
      if (!testCase) {
        throw new Error(`Test case ${testCaseId} not found`);
      }
      return testCase;
    },
    enabled: !!testCaseId,
    staleTime: 60000, // 1 minute
  });
}

// =============================================================================
// Grouping helpers
// =============================================================================

/** Statuses that count as "finished" — shown collapsed in the tester view */
const FINISHED_STATUSES: TestCaseStatus[] = ['closed', 'ready_to_validate'];

function isFinished(status: string): boolean {
  return FINISHED_STATUSES.includes(normalizeStatus(status) as TestCaseStatus);
}

/**
 * Given a sorted array of steps for one Excel file group, compute which steps
 * should be visible to the tester:
 *
 *   1. Finished steps (closed | ready_to_validate) → counted but hidden (collapsed)
 *   2. Blocked steps (waiting for preceding cross-user steps) → shown as locked
 *   3. First actionable step (unfinished + not blocked) → shown
 *   4. All remaining steps → hidden
 */
function computeVisibleSteps(sortedSteps: TestCase[]): {
  finishedSteps: TestCase[];
  visibleSteps: TestCase[];
  blockedSteps: TestCase[];
  futureSteps: TestCase[];
} {
  const finishedSteps: TestCase[] = [];
  const blockedSteps: TestCase[] = [];
  const futureSteps: TestCase[] = [];
  let currentStep: TestCase | null = null;

  for (const step of sortedSteps) {
    if (isFinished(step.status)) {
      finishedSteps.push(step);
    } else if (step.blocked) {
      blockedSteps.push(step);
    } else if (!currentStep) {
      currentStep = step;
    } else {
      futureSteps.push(step);
    }
  }

  return {
    finishedSteps,
    visibleSteps: currentStep ? [currentStep] : [],
    blockedSteps,
    futureSteps,
  };
}

/**
 * Groups flat test cases from the dashboard API into the hierarchical
 * Project → ExcelFile → Steps structure used by the tester view.
 */
export function groupTestCases(testCases: TestCase[]): ProjectGroup[] {
  // --- Step 1: group by project_id ---
  const projectMap = new Map<string, TestCase[]>();
  for (const tc of testCases) {
    const pid = tc.project_id ?? 'unknown';
    if (!projectMap.has(pid)) projectMap.set(pid, []);
    projectMap.get(pid)!.push(tc);
  }

  const projectGroups: ProjectGroup[] = [];

  for (const [project_id, projectSteps] of projectMap) {
    // Use project_name if available; the backend now backfills it from DynamoDB,
    // but keep the UUID fallback as a last resort for any edge cases.
    const project_name = projectSteps[0]?.project_name ?? project_id;
    const project_number = projectSteps[0]?.project_number;

    // --- Step 2: group by scenario_id (stable canonical key) ---
    // Using scenario_id instead of testscript_id because testscript_id may
    // be missing on test cases created via mappings_create (cascade_engine),
    // causing the same scenario to split into duplicate groups.
    const excelMap = new Map<string, TestCase[]>();
    for (const tc of projectSteps) {
      const key = tc.scenario_id ?? tc.testscript_id ?? 'unknown';
      if (!excelMap.has(key)) excelMap.set(key, []);
      excelMap.get(key)!.push(tc);
    }

    const excelGroups: ExcelFileGroup[] = [];

    for (const [groupKey, steps] of excelMap) {
      // Sort by step_number to preserve sequential ordering.
      // Step sequencing (which step is actionable) depends on this order;
      // due-date sorting is applied at the project-group level instead.
      const sorted = [...steps].sort((a, b) =>
        (a.step_number ?? 0) - (b.step_number ?? 0),
      );
      const { finishedSteps, visibleSteps, blockedSteps, futureSteps } = computeVisibleSteps(sorted);

      const hasError = visibleSteps.some(s => normalizeStatus(s.status) === 'errors');
      const isComplete = finishedSteps.length === sorted.length && blockedSteps.length === 0;

      const rawFileName = steps[0]?.sharepoint_file_name;
      // Strip .xlsx/.xls extension for display (e.g. "Login_Flow_v3.xlsx" → "Login_Flow_v3")
      const fileNameDisplay = rawFileName
        ? rawFileName.replace(/\.xlsx?$/i, '')
        : undefined;

      // Fallback chain for scenario name:
      // 1. sharepoint_file_name without extension — the actual file on SharePoint,
      //    matches what the "Open Testscript" link points to and what step data comes from
      // 2. scenario_name (from Excel's internal "Project:" header — may be stale
      //    if the template was copied without updating the header)
      // 3. testscript_id (e.g. "PRJ-001-TS001") — readable but opaque
      // 4. groupKey (last resort — may be a scenario UUID for very old records)
      const scenario_name =
        fileNameDisplay ??
        steps[0]?.scenario_name ??
        steps[0]?.testscript_id ??
        groupKey;

      excelGroups.push({
        groupKey,
        testscript_id: steps[0]?.testscript_id,
        scenario_name,
        scenario_id: steps[0]?.scenario_id ?? groupKey,
        sharepoint_file_url: steps[0]?.sharepoint_file_url,
        sharepoint_file_name: rawFileName,
        allSteps: sorted,
        finishedSteps,
        visibleSteps,
        blockedSteps,
        finishedCount: finishedSteps.length,
        blockedCount: blockedSteps.length,
        futureSteps,
        futureCount: futureSteps.length,
        hasError,
        isComplete,
      });
    }

    // Preserve insertion order from the backend's due_date ASC sort.
    // The first excel group encountered has the earliest actionable due date.

    // --- Step 3: aggregate project stats ---
    const total = projectSteps.length;
    const finished = projectSteps.filter(s => isFinished(s.status)).length;
    const errors = projectSteps.filter(s => normalizeStatus(s.status) === 'errors').length;
    const blocked = projectSteps.filter(s => s.blocked).length;
    const active = projectSteps.filter(s => {
      if (s.blocked) return false;
      const s2 = normalizeStatus(s.status);
      return s2 === 'ongoing' || s2 === 'new' || s2 === 'retest';
    }).length;

    projectGroups.push({
      project_id,
      project_name,
      project_number,
      excelGroups,
      stats: {
        total,
        finished,
        errors,
        active,
        blocked,
        progress_percentage: total > 0 ? Math.round((finished / total) * 100) : 0,
      },
    });
  }

  // Preserve insertion order from the backend's due_date ASC sort.
  // The first project encountered has the earliest actionable due date.

  return projectGroups;
}

/**
 * Hook that fetches the tester dashboard and returns test cases grouped
 * into the Project → ExcelFile → Steps hierarchy.
 */
export function useGroupedTestCases(options?: {
  userId?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  dueFilter?: 'all' | 'due' | 'overdue';
  view?: 'actionable' | 'all';
  scenario?: string | string[];
  search?: string;
  refetchInterval?: number;
}) {
  const query = useTesterDashboard({
    userId: options?.userId,
    page: options?.page,
    pageSize: options?.pageSize,
    sort: options?.sort,
    dueFilter: options?.dueFilter,
    view: options?.view,
    scenario: options?.scenario,
    search: options?.search,
    refetchInterval: options?.refetchInterval,
  });

  const projectGroups = useMemo(
    () => groupTestCases(query.data?.test_cases ?? []),
    [query.data?.test_cases],
  );

  const totalItems = query.data?.total ?? 0;
  const totalPages = query.data?.total_pages ?? 1;
  const pagination = query.data?.pagination;

  return { ...query, projectGroups, totalItems, totalPages, pagination, isFetching: query.isFetching };
}
